import type { ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import React, { forwardRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import localStyles from './PaneledItemList.module.pcss';
import type {
  PaneledItemListWrapperProps,
  PaneledItemListSectionProps,
  PaneledItemListSectionType
} from './PaneledItemListWrapperProps';

/**
 * A wrapper component for paneled item list. This component allows for subsections within it,
 * for a header, body and footer.
 */
const PaneledItemListWrapper: ForwardRefExoticComponent<
  PropsWithoutRef<PaneledItemListWrapperProps> & RefAttributes<HTMLElement>
> = forwardRef<HTMLElement, PaneledItemListWrapperProps>(function PanelForward(
  { type = PanelType.STANDARD, className, id, children, ...props },
  ref
) {
  const cx = useClassNameMapper(localStyles);
  return (
    <article
      ref={ref}
      id={id}
      className={cx(className, `lia-panel-list-${type} text-body`)}
      // support passing data-testid
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </article>
  );
});

function createPanelSection(
  section: PaneledItemListSectionType,
  asDefault: React.ElementType
): React.FC<React.PropsWithChildren<PaneledItemListSectionProps>> {
  return function PanelSectionFactory({
    className,
    children,
    as: As = asDefault
  }: PaneledItemListSectionProps): React.ReactElement {
    const cx = useClassNameMapper(localStyles);
    return <As className={cx(className, `lia-panel-list-${section}`)}>{children}</As>;
  };
}

const PaneledItemListHeader = createPanelSection('header', 'header');
const PaneledItemListBody = createPanelSection('body', 'section');
const PaneledItemListFooter = createPanelSection('footer', 'footer');

export {
  PaneledItemListWrapper as default,
  PaneledItemListHeader,
  PaneledItemListFooter,
  PaneledItemListBody
};
